import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ClearIcon from '@mui/icons-material/Clear';
import { IconButton, TextField } from '@mui/material';
import { format, isValid } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';

import type { ChangeEvent, KeyboardEvent } from 'react';
import type { Theme } from '@mui/material';
import type { SxProps } from '@mui/system';
export type MaybeDate = Date | undefined;
export type AnyDate = number | string | MaybeDate;
export type DateInputProps = {
  label?: string;
  value?: AnyDate;
  required?: boolean;
  onChange?: (v: MaybeDate) => void;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  errorText?: string;
  size?: 'small' | 'medium';
  hideClearButton?: boolean;
};

export const DATE_INPUT_PLACEHOLDER_TEXT = 'DD/MM/YYYY';
export const DATE_INPUT_TODAY_BUTTON = 'Today';

export const anyDateToMaybeDate = (value: AnyDate): MaybeDate => {
  const tryDate = value && new Date(value);
  const date: MaybeDate = tryDate && isValid(tryDate) ? tryDate : undefined;
  return date;
};

export const formatDate = (d: AnyDate): string => {
  const date = d && new Date(d);
  // TODO: consider locale
  return date && isValid(date) ? format(date, 'dd/MM/y') : '';
};

export default function DateInput({
  label = '',
  value,
  required,
  onChange,
  disabled,
  sx,
  errorText,
  size = 'medium',
  hideClearButton = false,
}: DateInputProps) {
  const [text, setText] = useState<string>(formatDate(value));
  const [date, _setDate] = useState<MaybeDate>();
  const [textIsError, setTextIsError] = useState<boolean>(false);
  const [lastKey, setLastKey] = useState<string>('');

  const setDate = useCallback(
    (d: MaybeDate) => {
      if (d?.getTime() == date?.getTime()) return;
      _setDate(d);
      if (onChange) {
        onChange(d);
      }
    },
    [onChange, date],
  );

  const onValueChange = useCallback(
    (date: MaybeDate) => {
      setTextIsError(false);
      setText(formatDate(date));
      setDate(date);
    },
    [setDate],
  );

  const handleTextChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      // help with user input
      let dateStr = e.target.value.trim() || '';
      const formatMatch = dateStr.match(/^\d{1,2}\/\d{1,2}\/\d{4}/);
      if (formatMatch) {
        dateStr = dateStr.slice(0, formatMatch[0].length);
      } else if (!['Backspace', 'Delete'].includes(lastKey)) {
        if (dateStr.match(/^\d{2}(\/\d{2})?$/)) {
          dateStr += '/';
        } else {
          dateStr = dateStr.replace(/\/+/g, '/');
        }
      }
      setText(dateStr);

      // set the picked date to a valid date or undefined
      const isoDate = formatMatch ? dateStr.split(/[/-]/, 3).reverse().join('/') : undefined;
      const newDate = anyDateToMaybeDate(isoDate);
      setTextIsError(dateStr.length > 0 && !newDate);
      setDate(newDate);
    },
    [lastKey, setDate],
  );

  const clearText = useCallback(() => {
    setLastKey('');
    handleTextChange({ target: { value: '' } } as ChangeEvent<HTMLInputElement>);
  }, [handleTextChange]);

  const setToday = useCallback(() => {
    onValueChange(new Date());
  }, [onValueChange]);

  const handleTextKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => setLastKey(e.key), []);

  useEffect(() => {
    const date = anyDateToMaybeDate(value);
    onValueChange(date);
  }, [value, onValueChange]);

  return (
    <TextField
      name={label}
      label={label}
      size={size}
      variant="outlined"
      placeholder={DATE_INPUT_PLACEHOLDER_TEXT}
      sx={sx}
      value={text}
      onKeyDown={handleTextKeyDown}
      onChange={handleTextChange}
      error={textIsError || Boolean(errorText) || (required && !text)}
      helperText={errorText}
      disabled={disabled}
      required={required}
      InputProps={{
        endAdornment: (
          <>
            {!hideClearButton && !!text.length && (
              <IconButton name="clear" title="Clear" disabled={disabled} onClick={clearText} sx={{ paddingRight: 0 }}>
                <ClearIcon />
              </IconButton>
            )}

            <IconButton name="today" title={DATE_INPUT_TODAY_BUTTON} disabled={disabled} onClick={setToday}>
              <CalendarTodayIcon />
            </IconButton>
          </>
        ),
      }}
    />
  );
}
